import { useRecoilValue } from 'recoil';
import type { SeriesInstanceUid } from '~/domain/dicom/uids';
import { getDatasetEnvironmentRegistry } from '~/engine/scene/datasetEnvironmentRegistryInstance';
import useAvailableDoses from '~/hooks/dose/useAvailableDoses';
import { useSelectedPrimaryImage } from '~/hooks/image/useSelectedImages';
import { currentDoseIdState } from '~/state/atoms/dose/doseAtoms';
import DoseControl from './DoseControl';
import { isDoseOfImage } from './dosesOfImage';

/**
 * The dose in a row's footer, after the row image's own controls. It shows
 * on a row whose image draws the dose, and on the primary image's row while
 * that image has doses to load. Another row offers no dose to load: loading
 * a dose of its image would make that image's structure set the active one.
 */
export default function RowDoseControl({
    seriesInstanceUid,
}: {
    /** The image the row draws. */
    seriesInstanceUid: SeriesInstanceUid;
}) {
    // A dose load or unload replaces the row's dose without a registry
    // notification; the selected dose id is what re-reads it.
    useRecoilValue(currentDoseIdState);
    const drawnDose =
        getDatasetEnvironmentRegistry().entryFor(seriesInstanceUid)?.dose;
    const primaryImage = useSelectedPrimaryImage();
    const doses = useAvailableDoses(true);
    const offered = doses.filter(
        ({ item }) =>
            item !== undefined && isDoseOfImage(item, seriesInstanceUid)
    );
    const isPrimaryRow = primaryImage?.seriesUid === seriesInstanceUid;

    if (!drawnDose && !(isPrimaryRow && offered.length > 0)) {
        return null;
    }
    return <DoseControl dose={drawnDose} offered={offered} />;
}
